// Optimistic cart UI — builds placeholder line items from cached product data
// and injects them into the cart drawer for instant visual feedback.

import {
	getCachedProductById,
	getCachedProductByVariantId,
	getTriggeredOfferRules,
	type OfferRule,
} from '@/scripts/core/product-cache'
import { getCartState, isCartStateInitialized } from '@/scripts/cart/cart-state'
import { cartDebug, summarizeCartItems } from '@/scripts/cart/optimistic-debug'
import type { ShopifyProductJSON } from '@/scripts/core/product-resolver'

export type OptimisticItemData = {
	variantId: number
	title: string
	variantTitle: string | null
	price: number // cents
	compareAtPrice: number | null // cents — when set and > price, shows sale UI
	quantity: number
	imageUrl: string | null
	productUrl: string // /products/{handle}
	isExclusive: boolean // bundle or membership — triggers eviction of existing exclusives
	/** Sort group matching server-side cart-item-sort-group.liquid:
	 *  1=bundle, 2=membership, 3=gwp, 4=regular */
	sortGroup: number
	/** True when product data is unavailable — renders a skeleton placeholder row */
	isSkeleton?: boolean
}

function readImageUrl(value: unknown): string | null {
	if (!value) return null
	if (typeof value === 'string') return value
	if (typeof value !== 'object') return null

	const record = value as Record<string, unknown>
	const src = record.src
	if (typeof src === 'string') return src

	const url = record.url
	if (typeof url === 'string') return url

	return null
}

function resolveOptimisticImageUrl(params: {
	product: ShopifyProductJSON
	variant: ShopifyProductJSON['variants'][number]
}): string | null {
	const { product, variant } = params

	const variantImage = readImageUrl(variant.featured_image as unknown)
	if (variantImage) return variantImage

	const productFeatured = readImageUrl(
		(product as Record<string, unknown>).featured_image
	)
	if (productFeatured) return productFeatured

	const productImage = readImageUrl(
		(product as Record<string, unknown>).image
	)
	if (productImage) return productImage

	const images = (product as Record<string, unknown>).images
	if (Array.isArray(images) && images.length > 0) {
		const firstImage = readImageUrl(images[0])
		if (firstImage) return firstImage
	}

	return null
}

function resolveOptimisticImageUrlFromProduct(
	product: ShopifyProductJSON
): string | null {
	const productFeatured = readImageUrl(
		(product as Record<string, unknown>).featured_image
	)
	if (productFeatured) return productFeatured

	const productImage = readImageUrl(
		(product as Record<string, unknown>).image
	)
	if (productImage) return productImage

	const images = (product as Record<string, unknown>).images
	if (Array.isArray(images) && images.length > 0) {
		return readImageUrl(images[0])
	}

	return null
}

/**
 * Build optimistic item data from a variant ID using the product cache.
 * Returns null when product data isn't available (caller should fall back to spinner).
 * Falls back to product ID lookup and first-variant match when the direct variant
 * index is cold — e.g., when a pay-later button's variant ID wasn't indexed by
 * the seed but the product itself is in cache via another variant.
 */
export function buildOptimisticItemData(params: {
	variantId: number
	product?: ShopifyProductJSON
	quantity?: number
	isTrial?: boolean
}): OptimisticItemData | null {
	const { variantId, quantity = 1, isTrial = false } = params

	// Primary: by-variant lookup against the cache.
	let product = params.product ?? getCachedProductByVariantId(variantId)

	// Fallback: maybe the variant ID is actually a product ID (e.g. cartApiCheckout
	// paths that pass product IDs through the variant slot), or the seed indexed
	// the product but not this specific variant.
	if (!product) {
		product = getCachedProductById(variantId)
	}

	if (!product) {
		cartDebug('optimistic-cart:build-item:no-product', {
			variantId,
			hasParamsProduct: Boolean(params.product),
		})
		return null
	}

	// Try to find the exact variant.
	let variant = product.variants.find((v) => v.id === variantId)

	// Fallback: variant not in cached product (common when the seed's collection
	// context returned a slimmer variant array than what the server uses at add
	// time). Use the first available variant's data so we can still render a
	// "real-looking" row. The server-rendered section will atomically replace it.
	if (!variant) {
		variant = product.variants.find((v) => v.available) ?? product.variants[0]
		cartDebug('optimistic-cart:build-item:variant-fallback', {
			variantId,
			productId: product.id,
			cachedVariantIds: product.variants.map((v) => v.id),
			fallbackVariantId: variant?.id,
		})
	}
	if (!variant) return null

	const imageUrl = resolveOptimisticImageUrl({
		product,
		variant,
	})

	const productType = String(
		(product as { type?: string; product_type?: string }).type ??
			(product as { type?: string; product_type?: string })
				.product_type ??
			''
	).toLowerCase()
	const handleLower = String(
		(product as { handle?: string }).handle ?? ''
	).toLowerCase()
	const titleLower = String(
		(product as { title?: string }).title ?? ''
	).toLowerCase()
	const isExclusive =
		productType === 'bundle' ||
		handleLower.includes('bundle') ||
		productType === 'streaming' ||
		titleLower.includes('bundle') ||
		titleLower.includes('membership') ||
		isTrial

	// Mirror server-side cart-item-sort-group.liquid:
	// 1=bundle, 2=membership, 3=gwp, 4=regular
	let sortGroup = 4
	if (
		productType === 'bundle' ||
		handleLower.includes('bundle') ||
		titleLower.includes('bundle')
	) {
		sortGroup = 1
	} else if (
		productType === 'streaming' ||
		titleLower.includes('membership')
	) {
		sortGroup = 2
	}

	return {
		variantId,
		title: product.title,
		variantTitle: variant.title !== 'Default Title' ? variant.title : null,
		price: variant.price,
		compareAtPrice: variant.compare_at_price ?? null,
		quantity,
		imageUrl,
		productUrl: `/products/${product.handle}`,
		isExclusive,
		sortGroup,
	}
}

type EvalCartItem = {
	productId: number
	title: string
	properties: Record<string, string>
}

type OfferSelection = {
	rule: OfferRule
	bundleParentId?: number
	bundleParentTitle?: string
	triggerProductTitle?: string
}

function hasPropValue(value: string | undefined): boolean {
	return value != null && value !== ''
}

function isDateValid(rule: OfferRule): boolean {
	const today = new Date().toISOString().slice(0, 10)
	if (rule.startDate && today < rule.startDate) return false
	if (rule.endDate && today > rule.endDate) return false
	return true
}

function hasTriggerProduct(
	rule: OfferRule,
	cartItems: EvalCartItem[]
): boolean {
	if (!rule.triggerProductIds.length) return false
	return cartItems.some((item) =>
		rule.triggerProductIds.includes(item.productId)
	)
}

function isOfferManagedLine(item: EvalCartItem): boolean {
	return (
		hasPropValue(item.properties._gwp_item) ||
		hasPropValue(item.properties._bundle_membership_item) ||
		hasPropValue(item.properties._alcm_item)
	)
}

function isOfferClaimed(rule: OfferRule, cartItems: EvalCartItem[]): boolean {
	return cartItems.some((item) => {
		if (!isOfferManagedLine(item)) return false
		const offerHandle =
			item.properties._bundle_offer ?? item.properties._gwp_offer
		return offerHandle === rule.handle
	})
}

function findMembershipTriggerMatch(
	rule: OfferRule,
	cartItems: EvalCartItem[]
): { bundleParentId: number; bundleParentTitle: string } | null {
	if (!rule.triggerProductIds.length) return null

	for (const triggerId of rule.triggerProductIds) {
		for (const item of cartItems) {
			if (item.productId !== triggerId) continue
			if (hasPropValue(item.properties._gwp_item)) continue
			if (hasPropValue(item.properties._bundle_membership_item)) continue
			if (hasPropValue(item.properties._alcm_item)) continue
			if (hasPropValue(item.properties._activation_item)) continue
			if (hasPropValue(item.properties._purchase_as_gift)) continue

			const payLater = item.properties._pay_later ?? ''
			if (
				payLater === 'paypal' ||
				payLater === 'afterpay' ||
				payLater === 'truemed'
			)
				continue

			return {
				bundleParentId: item.productId,
				bundleParentTitle: item.title,
			}
		}
	}

	return null
}

function findGwpTriggerMatch(
	rule: OfferRule,
	cartItems: EvalCartItem[]
): { triggerProductTitle: string; triggerProductId: number } | null {
	if (!rule.triggerProductIds.length) return null

	for (const item of cartItems) {
		for (const triggerId of rule.triggerProductIds) {
			if (item.productId !== triggerId) continue
			if (hasPropValue(item.properties._purchase_as_gift)) continue
			if (item.properties._pay_later === 'truemed') continue

			return {
				triggerProductTitle: item.title,
				triggerProductId: item.productId,
			}
		}
	}

	return null
}

function hasMembershipIncompatiblePayLaterLine(
	cartItems: EvalCartItem[]
): boolean {
	return cartItems.some((item) => {
		const payLater = item.properties._pay_later ?? ''
		return (
			payLater === 'afterpay' ||
			payLater === 'paypal' ||
			payLater === 'truemed'
		)
	})
}

function hasBundledMembershipLine(cartItems: EvalCartItem[]): boolean {
	return cartItems.some(
		(item) =>
			hasPropValue(item.properties._bundle_membership_item) ||
			hasPropValue(item.properties._alcm_item) ||
			hasPropValue(item.properties._activation_item)
	)
}

function sortRulesByPriority(rules: OfferRule[]): OfferRule[] {
	return [...rules].sort(
		(a, b) => (a.priority ?? 9999) - (b.priority ?? 9999)
	)
}

function buildEvalCartItems(params: {
	productId: number
	productTitle: string
	payLater?: string | null
	purchaseAsGift?: boolean
	isTrial?: boolean
}): EvalCartItem[] {
	const items: EvalCartItem[] = isCartStateInitialized()
		? getCartState().items.map((item) => ({
				productId: item.productId,
				title: item.title,
				properties: item.properties ?? {},
			}))
		: []

	const pendingProperties: Record<string, string> = {}
	if (params.payLater) pendingProperties._pay_later = params.payLater
	if (params.purchaseAsGift) pendingProperties._purchase_as_gift = 'true'
	if (params.isTrial) pendingProperties._is_trial = 'true'

	items.push({
		productId: params.productId,
		title: params.productTitle,
		properties: pendingProperties,
	})

	return items
}

function selectGwpOffer(
	rules: OfferRule[],
	cartItems: EvalCartItem[]
): OfferSelection | null {
	for (const rule of sortRulesByPriority(rules)) {
		if (rule.type !== 'gwp') continue
		if (!isDateValid(rule)) continue
		if (!hasTriggerProduct(rule, cartItems)) continue
		if (isOfferClaimed(rule, cartItems)) continue

		const triggerMatch = findGwpTriggerMatch(rule, cartItems)
		if (!triggerMatch) continue

		const giftAlreadyInCart = cartItems.some(
			(item) =>
				hasPropValue(item.properties._gwp_item) &&
				item.productId === rule.giftProduct.id
		)
		if (giftAlreadyInCart) continue

		return {
			rule,
			triggerProductTitle: triggerMatch.triggerProductTitle,
			bundleParentId: triggerMatch.triggerProductId,
		}
	}

	return null
}

function selectMembershipOffer(
	rules: OfferRule[],
	cartItems: EvalCartItem[],
	options?: { allowExistingBundledMembership?: boolean }
): OfferSelection | null {
	if (hasMembershipIncompatiblePayLaterLine(cartItems)) return null
	if (
		hasBundledMembershipLine(cartItems) &&
		!options?.allowExistingBundledMembership
	)
		return null

	for (const rule of sortRulesByPriority(rules)) {
		if (rule.type !== 'bundle_membership') continue
		if (!isDateValid(rule)) continue
		if (!hasTriggerProduct(rule, cartItems)) continue
		if (isOfferClaimed(rule, cartItems)) continue

		const match = findMembershipTriggerMatch(rule, cartItems)
		if (!match) continue

		return {
			rule,
			bundleParentId: match.bundleParentId,
			bundleParentTitle: match.bundleParentTitle,
		}
	}

	for (const rule of sortRulesByPriority(rules)) {
		if (rule.type !== 'alcm') continue
		if (!isDateValid(rule)) continue
		if (!hasTriggerProduct(rule, cartItems)) continue
		if (isOfferClaimed(rule, cartItems)) continue

		const match = findMembershipTriggerMatch(rule, cartItems)
		if (!match) continue

		return {
			rule,
			bundleParentId: match.bundleParentId,
			bundleParentTitle: match.bundleParentTitle,
		}
	}

	return null
}

/**
 * Build optimistic items for the product being added AND any predicted
 * associated items (memberships, GWP) based on client-side offer rules.
 * Applies the same business rules as the server-side offer evaluation:
 * - TrueMed blocks all offers
 * - Pay-later / gift-purchase blocks memberships
 * - Bundle membership takes precedence over ALCM
 * - Memberships are skipped when the cart already has one (checked via cart snapshot)
 * Returns null on cache miss.
 */
function buildSkeletonMain(
	variantId: number,
	quantity: number
): OptimisticItemData {
	return {
		variantId,
		title: '',
		variantTitle: null,
		price: 0,
		compareAtPrice: null,
		quantity,
		imageUrl: null,
		productUrl: '#',
		isExclusive: true, // defensive — ensures exclusive conflict resolution runs server-side
		sortGroup: 4,
		isSkeleton: true,
	}
}

export function buildOptimisticCartItems(params: {
	variantId: number
	product?: ShopifyProductJSON
	quantity?: number
	payLater?: string | null
	purchaseAsGift?: boolean
	isTrial?: boolean
}): {
	main: OptimisticItemData
	associated: AssociatedOptimisticItem[]
} {
	cartDebug('optimistic-cart:build:start', {
		variantId: params.variantId,
		hasParamsProduct: Boolean(params.product),
		quantity: params.quantity ?? 1,
		payLater: params.payLater ?? null,
		purchaseAsGift: params.purchaseAsGift ?? false,
	})
	const main = buildOptimisticItemData(params)
	if (!main) {
		const cachedByVariant = getCachedProductByVariantId(params.variantId)
		const cachedById = getCachedProductById(params.variantId)
		cartDebug('optimistic-cart:build:skeleton-fallback', {
			variantId: params.variantId,
			cachedByVariant: cachedByVariant
				? { id: cachedByVariant.id, handle: cachedByVariant.handle, variantCount: cachedByVariant.variants.length }
				: null,
			cachedById: cachedById
				? { id: cachedById.id, handle: cachedById.handle, variantCount: cachedById.variants.length }
				: null,
		})
		return {
			main: buildSkeletonMain(params.variantId, params.quantity ?? 1),
			associated: [],
		}
	}

	const product =
		params.product ?? getCachedProductByVariantId(params.variantId)
	const associated: AssociatedOptimisticItem[] = []

	if (product) {
		// TrueMed blocks all offers
		if (params.payLater === 'truemed') {
			cartDebug('optimistic-cart:build:truemed-skip-offers', {
				mainVariantId: main.variantId,
			})
			return { main, associated }
		}

		const rules = getTriggeredOfferRules(product.id, params.variantId)
		const evalCartItems = buildEvalCartItems({
			productId: product.id,
			productTitle: product.title,
			payLater: params.payLater,
			purchaseAsGift: params.purchaseAsGift,
			isTrial: params.isTrial,
		})

		const gwpSelection = selectGwpOffer(rules, evalCartItems)
		cartDebug('optimistic-cart:offer-eval:gwp', {
			productId: product.id,
			selectedHandle: gwpSelection?.rule.handle ?? null,
			selectedVariantId: gwpSelection?.rule.giftProduct.variantId ?? null,
		})
		if (gwpSelection) {
			associated.push({
				type: 'gwp',
				variantId: gwpSelection.rule.giftProduct.variantId,
				title: gwpSelection.rule.giftProduct.title,
				imageUrl: gwpSelection.rule.giftProduct.featured_image ?? null,
				productUrl: `/products/${gwpSelection.rule.giftProduct.handle}`,
				displayCta: gwpSelection.rule.displayCta,
			})
		}

		const productType = String(
			(product as { type?: string; product_type?: string }).type ??
				(product as { type?: string; product_type?: string })
					.product_type ??
				''
		).toLowerCase()
		const productTitle = String(
			(product as { title?: string }).title ?? ''
		).toLowerCase()
		const productIsMembership =
			productType === 'streaming' || productTitle.includes('membership')

		if (
			!productIsMembership &&
			!params.payLater &&
			!params.purchaseAsGift
		) {
			const allowExistingBundledMembership = main.sortGroup === 1
			const membershipSelection = selectMembershipOffer(
				rules,
				evalCartItems,
				{ allowExistingBundledMembership }
			)
			cartDebug('optimistic-cart:offer-eval:membership', {
				productId: product.id,
				allowExistingBundledMembership,
				selectedType: membershipSelection?.rule.type ?? null,
				selectedHandle: membershipSelection?.rule.handle ?? null,
				selectedVariantId:
					membershipSelection?.rule.giftProduct.variantId ?? null,
			})
			if (membershipSelection) {
				associated.push({
					type: membershipSelection.rule.type,
					variantId: membershipSelection.rule.giftProduct.variantId,
					title: membershipSelection.rule.giftProduct.title,
					imageUrl:
						membershipSelection.rule.giftProduct.featured_image ??
						null,
					productUrl: `/products/${membershipSelection.rule.giftProduct.handle}`,
					displayCta: membershipSelection.rule.displayCta,
				})
			}
		}
	}

	cartDebug('optimistic-cart:build:result', {
		main: {
			variantId: main.variantId,
			title: main.title,
			sortGroup: main.sortGroup,
			isExclusive: main.isExclusive,
		},
		associated: associated.map((item) => ({
			type: item.type,
			variantId: item.variantId,
			title: item.title,
		})),
	})

	return { main, associated }
}

export type AssociatedOptimisticItem = {
	type: 'gwp' | 'bundle_membership' | 'alcm'
	variantId?: number
	title: string
	imageUrl: string | null
	productUrl: string
	displayCta: string
}

/**
 * Create an optimistic DOM element for an associated item (membership or GWP).
 * Matches the real Liquid template layout.
 */
export function createAssociatedOptimisticElement(
	data: AssociatedOptimisticItem
): HTMLDivElement {
	const el = document.createElement('div')
	el.setAttribute('data-optimistic-item', '')
	el.setAttribute('data-optimistic-associated', '')
	if (data.variantId) {
		el.setAttribute('data-optimistic-variant-id', String(data.variantId))
	}
	el.setAttribute('data-uc-cart-item', '')
	// Set sort group: memberships=2, gwp=3
	const sortGroup =
		data.type === 'bundle_membership' || data.type === 'alcm' ? 2 : 3
	el.setAttribute('data-optimistic-sort-group', String(sortGroup))
	const safeTitle = escapeHTML(data.title)

	const imgInner = data.imageUrl
		? `<div class="cart-item__image block"><img src="${data.imageUrl}" alt="${safeTitle}" width="128" height="128" loading="eager" style="width:100%;height:auto;aspect-ratio:1;object-fit:cover;"></div>`
		: ''

	if (data.type === 'bundle_membership' || data.type === 'alcm') {
		el.className = 'cart-free_product'
		el.innerHTML = `
			<div class="cart-free_product-image">
				<div class="cart-item__media">${imgInner}</div>
			</div>
			<div class="cart-free_product-info">
				<div class="cart-free_product-content">
					<div class="cart-free_product-title flex justify-between w-100" style="max-width: unset;">
						<span>${safeTitle}</span>
					</div>
					<div class="cart-free_product-footer">
						<div class="cart-free_product-purchase">Included with purchase</div>
					</div>
				</div>
			</div>
		`
	} else {
		// GWP
		el.className = 'cart-item'
		el.innerHTML = `
			<div class="cart-item__media">
				${data.imageUrl ? `<a href="${data.productUrl}" class="cart-item__link" tabindex="-1" aria-hidden="true"><span></span></a>${imgInner}` : ''}
			</div>
			<div class="cart-item__content flex flex-column justify-between">
				<div class="cart-item__details flex flex-column">
					<div class="cart-item__heading">
						<div class="flex flex-column justify-start">
							<a href="${data.productUrl}" class="link break no-underline sub-head" data-uc-cart-item-name>${safeTitle}</a>
						</div>
					</div>
					<div class="cart-item__heading cart-item__savings">
						<span>${escapeHTML(data.displayCta)}</span>
					</div>
					<div class="cart-item__heading">
						<p>Your Price</p>
						<p class="price price--end" data-cart-line-price>${LOADING_SPINNER_HTML}</p>
					</div>
				</div>
			</div>
		`
	}

	return el
}

/** Format price in cents to dollar string (e.g. 2500 → "$25.00"). */
function formatMoney(cents: number): string {
	return `$${(cents / 100).toFixed(2)}`
}

function escapeHTML(str: string): string {
	const el = document.createElement('span')
	el.textContent = str
	return el.innerHTML
}

/**
 * Create a DOM element that matches the real cart-item structure exactly.
 * Includes quantity controls and remove button so it looks fully settled in the cart.
 * Controls are non-functional — the server re-render replaces this element.
 */
export function createOptimisticItemElement(
	data: OptimisticItemData
): HTMLDivElement {
	const el = document.createElement('div')
	el.className = 'cart-item'
	el.setAttribute('data-optimistic-item', '')
	el.setAttribute('data-uc-cart-item', '')
	el.setAttribute('data-optimistic-sort-group', String(data.sortGroup))

	if (data.isSkeleton) {
		el.setAttribute('data-optimistic-skeleton', '')
		el.setAttribute('aria-label', 'Adding to cart')
		el.innerHTML = `
			<div class="cart-item__media">
				<div class="cart-item__image cart-item__image--skeleton block"></div>
			</div>
			<div class="cart-item__content flex flex-column justify-between">
				<div class="cart-item__details flex flex-column">
					<div class="cart-item__heading">
						<div class="flex flex-column justify-start">
							<span class="cart-item__title-skeleton" aria-hidden="true"></span>
						</div>
					</div>
					<div class="cart-item__heading">
						<p>Your Price</p>
						<p class="price price--end" data-cart-line-price>${LOADING_SPINNER_HTML}</p>
					</div>
				</div>
			</div>
		`
		return el
	}

	const title = escapeHTML(data.title)

	const imgHTML = data.imageUrl
		? `<a href="${data.productUrl}" class="cart-item__link" tabindex="-1" aria-hidden="true"><span></span></a>
		   <div class="cart-item__image block"><img src="${data.imageUrl}" alt="${title}" width="128" height="128" loading="eager" style="width:100%;height:auto;aspect-ratio:1;object-fit:cover;"></div>`
		: ''

	const variantHTML = data.variantTitle
		? `<dl><div class="product-option"><dt></dt><dd>${escapeHTML(data.variantTitle)}</dd></div></dl>`
		: ''

	const hasSalePrice =
		data.compareAtPrice != null && data.compareAtPrice > data.price

	// Compare-at strikethrough price — inside the title heading, after the
	// flex column div, matching the Liquid cart-drawer-items template structure.
	const compareAtHTML = hasSalePrice
		? `<s class="cart-item__old-price price price--end" data-cart-line-price>${LOADING_SPINNER_HTML}</s>`
		: ''

	el.innerHTML = `
		<div class="cart-item__media">
			${imgHTML}
		</div>
		<div class="cart-item__content flex flex-column justify-between">
			<div class="cart-item__details flex flex-column">
				<div class="cart-item__heading">
					<div class="flex flex-column justify-start">
						<a href="${data.productUrl}" class="link break no-underline sub-head" data-uc-cart-item-name>
							${title}
						</a>
						${variantHTML}
					</div>
					${compareAtHTML}
				</div>
				<div class="cart-item__heading">
					<p>Your Price</p>
					<p class="price price--end" data-cart-line-price>${LOADING_SPINNER_HTML}</p>
				</div>
			</div>
			<div class="cart-item__quantity flex align-end">
				<div class="cart-item__quantity-wrapper justify-between align-end w-100">
					<quantity-input class="quantity cart-quantity">
						<button class="quantity__button no-js-hidden" name="minus" type="button" aria-disabled="true" style="opacity:0.5;pointer-events:none;">
							<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"/></svg>
						</button>
						<input data-uc-quantity-input type="number" data-quantity-variant-id="${data.variantId}" value="${data.quantity}" min="1" readonly aria-label="Quantity for ${title}">
						<button class="quantity__button no-js-hidden" name="plus" type="button" aria-disabled="true" style="opacity:0.5;pointer-events:none;">
							<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"/></svg>
						</button>
					</quantity-input>
					<button type="button" class="cart-remove-button" aria-label="Remove ${title}">Remove</button>
				</div>
			</div>
		</div>
	`

	return el
}

/**
 * Prepend an optimistic item element into the cart drawer's main container.
 * Removes the empty-cart attribute from both cart-drawer-items and its
 * cart-drawer parent so CSS doesn't hide the items container.
 */
export function injectOptimisticItem(
	drawerItemsEl: Element,
	element: HTMLDivElement,
	sortGroup: number = 4
): void {
	const main = drawerItemsEl.querySelector('[data-uc-cart-drawer-main]')
	if (!main) return

	// Remove empty state from cart-drawer-items AND the parent cart-drawer
	// (CSS: [data-uc-cart-empty] .cart__contents { display: none })
	drawerItemsEl.removeAttribute('data-uc-cart-empty')
	const cartDrawer = drawerItemsEl.closest('cart-drawer')
	if (cartDrawer) cartDrawer.removeAttribute('data-uc-cart-empty')

	// Hide the empty-cart message if present
	const emptyState = drawerItemsEl.querySelector('.drawer__cart-empty')
	if (emptyState instanceof HTMLElement) emptyState.style.display = 'none'

	// Find or create the items container
	let itemsContainer = main.querySelector('.cart__drawer-items')
	if (!itemsContainer) {
		itemsContainer = document.createElement('div')
		itemsContainer.className = 'table-items cart__drawer-items'
		main.prepend(itemsContainer)
	}

	// Insert at the correct position to match server sort order.
	// Server groups: 1=bundle, 2=membership, 3=gwp, 4=regular.
	// Each existing item has a data-sort-group attribute set by
	// the Liquid template, OR is a cart-free_product (group 2) or
	// has data-gwp-offer (group 3). Fall back to appending if
	// we can't determine the right position.
	const existingItems = itemsContainer.querySelectorAll('[data-uc-cart-item]')
	let insertBefore: Element | null = null

	for (const existing of existingItems) {
		const existingGroup = getItemSortGroup(existing)
		if (existingGroup > sortGroup) {
			insertBefore = existing
			break
		}
	}

	if (insertBefore) {
		itemsContainer.insertBefore(element, insertBefore)
	} else {
		itemsContainer.appendChild(element)
	}
}

/** Determine the sort group of an existing cart item in the DOM.
 *  Mirrors server-side cart-item-sort-group.liquid:
 *  1=bundle, 2=membership, 3=gwp, 4=regular */
function getItemSortGroup(el: Element): number {
	// Optimistic items carry their sort group explicitly
	if (el.hasAttribute('data-optimistic-sort-group')) {
		return parseInt(
			el.getAttribute('data-optimistic-sort-group') || '4',
			10
		)
	}
	// Server-rendered items carry data-sort-group from Liquid
	if (el.hasAttribute('data-sort-group')) {
		return parseInt(el.getAttribute('data-sort-group') || '4', 10)
	}
	// Fallback heuristics for items without the attribute
	if (
		el.classList.contains('cart-free_product') ||
		el.hasAttribute('data-bundle-offer')
	)
		return 2
	if (el.hasAttribute('data-gwp-offer')) return 3
	const nameEl = el.querySelector('[data-uc-cart-item-name]')
	if (nameEl?.textContent?.toLowerCase().includes('bundle')) return 1
	return 4
}

/**
 * Create optimistic elements for pending additions (GWP, bundle memberships, ALCM)
 * from the cart-update-instructions data. Uses the product cache for image/title
 * when available, otherwise builds a "Included with purchase" placeholder that
 * matches the cart-free_product layout used by the real Liquid template.
 */
export function createOptimisticAdditionElements(
	additions: {
		id: number
		quantity: number
		properties: Record<string, string>
	}[]
): HTMLDivElement[] {
	cartDebug('optimistic-cart:create-addition-elements:start', {
		additions: summarizeCartItems(additions),
	})
	const elements: HTMLDivElement[] = []

	for (const addition of additions) {
		const props = addition.properties ?? {}
		const isMembership =
			!!props._bundle_membership_item ||
			!!props._alcm_item ||
			!!props._activation_item
		const isGwp = !!props._gwp_item

		// Try to get product data from cache for image/title
		const cached = getCachedProductByVariantId(addition.id)
		const title =
			cached?.title ?? props._bundle_parent_title ?? 'Membership'
		const safeTitle = escapeHTML(title)
		const cachedVariant = cached?.variants.find((v) => v.id === addition.id)
		const imageUrl =
			cached && cachedVariant
				? resolveOptimisticImageUrl({
						product: cached,
						variant: cachedVariant,
					})
				: cached
					? resolveOptimisticImageUrlFromProduct(cached)
					: null
		const productUrl = cached ? `/products/${cached.handle}` : '#'

		const imgHTML = imageUrl
			? `<div class="cart-item__media">
				<a href="${productUrl}" class="cart-item__link" tabindex="-1" aria-hidden="true"><span></span></a>
				<div class="cart-item__image block"><img src="${imageUrl}" alt="${safeTitle}" width="128" height="128" loading="eager" style="width:100%;height:auto;aspect-ratio:1;object-fit:cover;"></div>
			</div>`
			: `<div class="cart-item__media"></div>`

		const el = document.createElement('div')
		el.setAttribute('data-optimistic-item', '')
		el.setAttribute('data-optimistic-associated', '')
		el.setAttribute('data-optimistic-variant-id', String(addition.id))
		el.setAttribute('data-uc-cart-item', '')
		el.setAttribute(
			'data-optimistic-sort-group',
			isMembership ? '2' : isGwp ? '3' : '4'
		)

		if (isMembership) {
			// Match the cart-free_product layout from the Liquid template
			el.className = 'cart-free_product'
			el.innerHTML = `
				<div class="cart-free_product-image">
					<div class="cart-item__media">
						${imageUrl ? `<div class="cart-item__image block"><img src="${imageUrl}" alt="${safeTitle}" width="128" height="128" loading="eager" style="width:100%;height:auto;aspect-ratio:1;object-fit:cover;"></div>` : ''}
					</div>
				</div>
				<div class="cart-free_product-info">
					<div class="cart-free_product-content">
						<div class="cart-free_product-title flex justify-between w-100" style="max-width: unset;">
							<span>${safeTitle}</span>
						</div>
						<div class="cart-free_product-footer">
							<div class="cart-free_product-purchase">Included with purchase</div>
						</div>
					</div>
				</div>
			`
		} else if (isGwp) {
			// Match the regular cart-item layout with GWP savings display
			const displayCta = escapeHTML(props._gwp_display_cta ?? 'Free Gift')
			el.className = 'cart-item'
			el.innerHTML = `
				${imgHTML}
				<div class="cart-item__content flex flex-column justify-between">
					<div class="cart-item__details flex flex-column">
						<div class="cart-item__heading">
							<div class="flex flex-column justify-start">
								<a href="${productUrl}" class="link break no-underline sub-head" data-uc-cart-item-name>${safeTitle}</a>
							</div>
						</div>
						<div class="cart-item__heading cart-item__savings">
							<span>${displayCta}</span>
						</div>
						<div class="cart-item__heading">
							<p>Your Price</p>
							<p class="price price--end" data-cart-line-price>${LOADING_SPINNER_HTML}</p>
						</div>
					</div>
				</div>
			`
		} else {
			continue // Unknown addition type — skip
		}

		elements.push(el)
	}

	cartDebug('optimistic-cart:create-addition-elements:done', {
		elementCount: elements.length,
	})

	return elements
}

/**
 * Inject optimistic associated items into the cart drawer using sort-group
 * ordering so they appear in the correct position (memberships before GWP,
 * both before regular products).
 */
export function injectOptimisticAdditions(
	drawerItemsEl: Element,
	elements: HTMLDivElement[]
): void {
	if (!elements.length) return
	for (const el of elements) {
		const sortGroup = parseInt(
			el.getAttribute('data-optimistic-sort-group') || '4',
			10
		)
		injectOptimisticItem(drawerItemsEl, el, sortGroup)
	}
}

/** Remove all optimistic item elements from a container. */
export function removeAllOptimisticItems(container: Element): void {
	container
		.querySelectorAll('[data-optimistic-item]')
		.forEach((el) => el.remove())
}

// ── Phase 1: Optimistic count, footer & progress bar ─────────────

/**
 * Increment the cart count badge(s) by addedQuantity.
 * Handles both filled-cart (span[aria-hidden]) and empty-cart (bare text "0") states.
 */
export function updateOptimisticCartCount(addedQuantity: number): void {
	if (addedQuantity <= 0) return
	const bubbles = document.querySelectorAll(
		'[data-uc-cart-icon-bubble] .cart-count-bubble'
	)
	for (const bubble of bubbles) {
		const visibleSpan = bubble.querySelector<HTMLElement>(
			'span[aria-hidden="true"]'
		)
		if (visibleSpan) {
			const current =
				parseInt(visibleSpan.textContent?.trim() || '0', 10) || 0
			const newCount = current + addedQuantity
			visibleSpan.textContent = String(newCount)
			const hiddenSpan =
				bubble.querySelector<HTMLElement>('.visually-hidden')
			if (hiddenSpan) {
				hiddenSpan.textContent =
					hiddenSpan.textContent?.replace(/\d+/, String(newCount)) ||
					`${newCount} items`
			}
		} else {
			// Empty cart — bubble contains just "0" as text
			const newCount = addedQuantity
			bubble.innerHTML = `<span aria-hidden="true">${newCount}</span><span class="visually-hidden">${newCount} items</span>`
		}
	}
}

const LOADING_SPINNER_HTML =
	'<span class="cart-amount-loading" aria-hidden="true"></span>'

/**
 * Set loading state on cart amount elements.
 * Shows a loading spinner instead of the calculated value.
 */
function setCartAmountLoading(
	element: HTMLElement | null,
	preserveText = false
): void {
	if (!element) return

	// Don't show loading if element already has loading state
	if (element.querySelector('.cart-amount-loading')) return

	if (preserveText && element.textContent) {
		// For checkout button, wrap just the amount part
		const amountSpan = element.querySelector('[data-cart-checkout-amount]')
		if (amountSpan) {
			amountSpan.innerHTML = LOADING_SPINNER_HTML
		}
	} else {
		element.innerHTML = LOADING_SPINNER_HTML
	}
}

/**
 * Clear loading state and set actual value.
 */
function clearCartAmountLoading(
	element: HTMLElement | null,
	value: string,
	isCheckoutButton = false
): void {
	if (!element) return

	if (isCheckoutButton) {
		const amountSpan = element.querySelector('[data-cart-checkout-amount]')
		if (amountSpan) {
			amountSpan.textContent = value
		} else {
			// Fallback: update entire text if span not found
			element.textContent =
				element.textContent?.replace(/•\s*\$[\d,.]+/, `• ${value}`) ??
				element.textContent
		}
	} else {
		element.textContent = value
	}
}

export function setCartLinePricesLoading(root: ParentNode = document): void {
	const priceEls = root.querySelectorAll<HTMLElement>(
		'[data-cart-line-price]'
	)
	for (const priceEl of priceEls) {
		setCartAmountLoading(priceEl)
	}
}

/**
 * Update cart footer during optimistic operations.
 * Instead of showing potentially incorrect calculated values,
 * we show loading indicators until server validates the cart.
 *
 * @param params - Object containing operation info
 * @param params.isLoading - Whether to show loading state (true) or update values (false)
 * @param params.displaySubtotalDelta - Delta for the compare-at style subtotal display (cents)
 * @param params.payableTotalDelta - Delta for the actual payable total (cents)
 */
export function updateOptimisticFooter(params: {
	isLoading: boolean
	displaySubtotalDelta?: number
	payableTotalDelta?: number
}): void {
	const { isLoading, displaySubtotalDelta, payableTotalDelta } = params

	// Un-hide the footer if it was hidden for the empty-cart state
	const hiddenFooter = document.querySelector<HTMLElement>(
		'[data-optimistic-footer-hidden]'
	)
	if (hiddenFooter) {
		hiddenFooter.style.removeProperty('display')
		hiddenFooter.removeAttribute('data-optimistic-footer-hidden')

		// Hide the empty-cart upsell product that sits above the footer
		const emptyUpsell = document.querySelector<HTMLElement>(
			'[data-empty-cart-upsell]'
		)
		if (emptyUpsell) emptyUpsell.style.display = 'none'
	}

	const subtotalEl = document.getElementById('cart-subtotal')
	const savingsEl = document.getElementById('cart-savings')
	const checkoutBtn = document.getElementById('CartDrawer-Checkout')

	if (isLoading) {
		// Show loading indicators
		setCartAmountLoading(subtotalEl)
		setCartAmountLoading(savingsEl)
		setCartAmountLoading(checkoutBtn, true)
	} else {
		// Update with calculated values (fallback for non-critical updates)
		if (displaySubtotalDelta && displaySubtotalDelta > 0 && subtotalEl) {
			const currentCents = parseMoney(
				subtotalEl.textContent?.trim() || '$0.00'
			)
			const newSubtotal = currentCents + displaySubtotalDelta
			clearCartAmountLoading(subtotalEl, formatMoney(newSubtotal))
		}

		if (payableTotalDelta && payableTotalDelta > 0 && checkoutBtn) {
			const currentTotalMatch =
				checkoutBtn.textContent?.match(/\$[\d,.]+/)
			const currentTotalCents = currentTotalMatch
				? parseMoney(currentTotalMatch[0])
				: parseMoney(checkoutBtn.textContent?.trim() || '$0.00')
			const newTotal = currentTotalCents + payableTotalDelta
			clearCartAmountLoading(checkoutBtn, formatMoney(newTotal), true)
		}
	}
}

/**
 * Update <dynamic-progress-bar> data-percent, CSS property, and status text.
 * Shows the bar if cart was empty (display:none).
 */
export function updateOptimisticProgressBar(addedPriceCents: number): void {
	if (addedPriceCents <= 0) return
	const bar = document.querySelector<HTMLElement>('dynamic-progress-bar')
	if (!bar) return

	const threshold = parseFloat(bar.getAttribute('data-threshold') || '0')
	if (threshold <= 0) return

	const currentPercent = parseFloat(bar.getAttribute('data-percent') || '0')
	const currentAmount = (currentPercent / 100) * threshold
	const newAmount = currentAmount + addedPriceCents
	const newPercent = Math.min(100, Math.round((newAmount / threshold) * 100))

	bar.setAttribute('data-percent', String(newPercent))
	bar.style.setProperty('--progress-percent', `${newPercent}%`)

	const statusEl = bar.querySelector<HTMLElement>(
		'[data-shipping-bar-status]'
	)
	if (statusEl) {
		if (newPercent >= 100) {
			const successMsg =
				bar.getAttribute('data-success') ||
				"You've unlocked FREE shipping"
			statusEl.innerHTML = successMsg
		} else {
			const remaining = threshold - newAmount
			statusEl.innerHTML = `You are ${formatMoney(remaining)} away from free shipping.`
		}
	}

	// If cart was empty, show progress bar and hide free shipping info
	if (bar.style.display === 'none') {
		bar.style.removeProperty('display')
		const freeShippingInfo = document.querySelector<HTMLElement>(
			'[data-free-shipping-info]'
		)
		if (freeShippingInfo)
			freeShippingInfo.style.setProperty('display', 'none')
	}
}

/**
 * Set absolute values for cart totals/counts (used by qty change/removal).
 * Unlike the incremental update* functions above, this sets values directly
 * from CartState rather than adding deltas.
 */
export function updateOptimisticTotals(totals: {
	displaySubtotal: number
	itemCount: number
	totalPrice: number
}): void {
	// Update count badges
	const bubbles = document.querySelectorAll(
		'[data-uc-cart-icon-bubble] .cart-count-bubble'
	)
	for (const bubble of bubbles) {
		const visibleSpan = bubble.querySelector<HTMLElement>(
			'span[aria-hidden="true"]'
		)
		if (visibleSpan) {
			visibleSpan.textContent = String(totals.itemCount)
			const hiddenSpan =
				bubble.querySelector<HTMLElement>('.visually-hidden')
			if (hiddenSpan) {
				hiddenSpan.textContent =
					hiddenSpan.textContent?.replace(
						/\d+/,
						String(totals.itemCount)
					) || `${totals.itemCount} items`
			}
		} else {
			bubble.innerHTML = `<span aria-hidden="true">${totals.itemCount}</span><span class="visually-hidden">${totals.itemCount} items</span>`
		}
	}

	// Update subtotal - clear loading state
	const subtotalEl = document.getElementById('cart-subtotal')
	if (subtotalEl) {
		clearCartAmountLoading(subtotalEl, formatMoney(totals.displaySubtotal))
	}

	// Update savings - clear loading state if element exists
	const savingsEl = document.getElementById('cart-savings')
	if (savingsEl) {
		// Calculate savings from displaySubtotal - totalPrice
		const savings = totals.displaySubtotal - totals.totalPrice
		if (savings > 0) {
			clearCartAmountLoading(savingsEl, `-${formatMoney(savings)}`)
		}
	}

	// Update checkout button - clear loading state
	const checkoutBtn = document.getElementById('CartDrawer-Checkout')
	if (checkoutBtn) {
		clearCartAmountLoading(
			checkoutBtn,
			formatMoney(totals.totalPrice),
			true
		)
	}

	// Update progress bar
	const bar = document.querySelector<HTMLElement>('dynamic-progress-bar')
	if (bar) {
		const threshold = parseFloat(bar.getAttribute('data-threshold') || '0')
		if (threshold > 0) {
			const newPercent = Math.min(
				100,
				Math.round((totals.displaySubtotal / threshold) * 100)
			)
			bar.setAttribute('data-percent', String(newPercent))
			bar.style.setProperty('--progress-percent', `${newPercent}%`)

			const statusEl = bar.querySelector<HTMLElement>(
				'[data-shipping-bar-status]'
			)
			if (statusEl) {
				if (newPercent >= 100) {
					const successMsg =
						bar.getAttribute('data-success') ||
						"You've unlocked FREE shipping"
					statusEl.innerHTML = successMsg
				} else {
					const remaining = threshold - totals.displaySubtotal
					statusEl.innerHTML = `You are ${formatMoney(remaining)} away from free shipping.`
				}
			}
		}
	}
}

/** Parse "$12.34" or "$1,234.56" → cents. */
function parseMoney(text: string): number {
	const cleaned = text.replace(/[^0-9.]/g, '')
	return Math.round(parseFloat(cleaned) * 100) || 0
}

// ── Checkout button lock state ───────────────────────────────────

/**
 * Disable the checkout button and show a loading spinner.
 * Called when an optimistic lock is acquired — prevents checkout
 * while the cart is still being validated server-side.
 */
export function disableCheckoutButton(): void {
	const btn = document.getElementById('CartDrawer-Checkout')
	if (!btn || btn.hasAttribute('data-optimistic-disabled')) return
	btn.setAttribute('data-optimistic-disabled', '')
	btn.setAttribute('disabled', '')
	const amountSpan = btn.querySelector<HTMLElement>(
		'[data-cart-checkout-amount]'
	)
	if (amountSpan && !amountSpan.hasAttribute('data-original-amount')) {
		amountSpan.setAttribute(
			'data-original-amount',
			amountSpan.textContent ?? ''
		)
	}
	setCartAmountLoading(btn, true)
}

/**
 * Re-enable the checkout button after the cart has been validated.
 * Called by finalRender after the atomic section swap — the server-
 * rendered button replaces this one, but we call this as a safety net
 * in case the section swap doesn't include the footer.
 */
export function enableCheckoutButton(): void {
	const btn = document.getElementById('CartDrawer-Checkout')
	if (!btn || !btn.hasAttribute('data-optimistic-disabled')) return
	btn.removeAttribute('data-optimistic-disabled')
	btn.removeAttribute('disabled')
	const amountSpan = btn.querySelector<HTMLElement>(
		'[data-cart-checkout-amount]'
	)
	const originalAmount = amountSpan?.getAttribute('data-original-amount')
	if (
		amountSpan &&
		originalAmount != null &&
		amountSpan.querySelector('.cart-amount-loading')
	) {
		amountSpan.textContent = originalAmount
	}
	if (amountSpan) {
		amountSpan.removeAttribute('data-original-amount')
	}
}

// ── Phase 3b: Predict GWP/membership additions ──────────────────

/** Items to add to cart — structurally identical to AddToCartItem in cart-functions. */
export type PredictedCartItem = {
	id: number
	quantity: number
	selling_plan?: number
	properties: Record<string, string>
}

/**
 * Build the list of GWP / bundle-membership / ALCM items that the server
 * would add after evaluating offer rules. These are batched into the
 * initial /cart/add.js request to eliminate the second round trip.
 */
export function buildPredictedAdditions(params: {
	productId: number
	productTitle: string
	payLater?: string | null
	purchaseAsGift?: boolean
}): PredictedCartItem[] {
	const { productId, productTitle, payLater, purchaseAsGift } = params
	if (!productId) return []

	// TrueMed blocks all offers
	if (payLater === 'truemed') return []

	const rules = getTriggeredOfferRules(productId)
	if (!rules.length) return []

	const evalCartItems = buildEvalCartItems({
		productId,
		productTitle,
		payLater,
		purchaseAsGift,
	})

	const items: PredictedCartItem[] = []

	const gwpSelection = selectGwpOffer(rules, evalCartItems)
	cartDebug('optimistic-cart:predict-additions:gwp', {
		productId,
		selectedHandle: gwpSelection?.rule.handle ?? null,
		selectedVariantId: gwpSelection?.rule.giftProduct.variantId ?? null,
	})
	if (gwpSelection) {
		items.push({
			id: gwpSelection.rule.giftProduct.variantId,
			quantity: 1,
			properties: {
				_gwp_item: 'FREE',
				_gwp_offer: gwpSelection.rule.handle,
				_gwp_trigger_product_title:
					gwpSelection.triggerProductTitle ?? productTitle,
				_gwp_display_cta: gwpSelection.rule.displayCta,
				_bundle_parent_id: String(
					gwpSelection.bundleParentId ?? productId
				),
			},
		})
	}

	const cachedProduct = getCachedProductById(productId)
	const productIsMembership =
		(cachedProduct?.type ?? '').toLowerCase() === 'streaming' ||
		productTitle.toLowerCase().includes('membership')
	const productType = (cachedProduct?.type ?? '').toLowerCase()
	const productHandle = (cachedProduct?.handle ?? '').toLowerCase()
	const productIsBundle =
		productType === 'bundle' ||
		productHandle.includes('bundle') ||
		productTitle.toLowerCase().includes('bundle')

	if (!productIsMembership && !payLater && !purchaseAsGift) {
		const membershipSelection = selectMembershipOffer(
			rules,
			evalCartItems,
			{
				allowExistingBundledMembership: productIsBundle,
			}
		)
		cartDebug('optimistic-cart:predict-additions:membership', {
			productId,
			allowExistingBundledMembership: productIsBundle,
			selectedType: membershipSelection?.rule.type ?? null,
			selectedHandle: membershipSelection?.rule.handle ?? null,
			selectedVariantId:
				membershipSelection?.rule.giftProduct.variantId ?? null,
		})
		if (membershipSelection) {
			const props: Record<string, string> = {
				_free_item: 'FREE',
				_gwp_offer: membershipSelection.rule.handle,
				_bundle_parent_id: String(
					membershipSelection.bundleParentId ?? productId
				),
				_bundle_parent_title:
					membershipSelection.bundleParentTitle ?? productTitle,
			}

			if (membershipSelection.rule.type === 'bundle_membership') {
				props._bundle_membership_item = 'true'
				props._bundle_membership_duration =
					membershipSelection.rule.giftProduct.membershipDuration ||
					'3_month'
			} else {
				props._alcm_item = 'true'
			}

			if (membershipSelection.rule.giftProduct.trialMetafields) {
				const tm = membershipSelection.rule.giftProduct.trialMetafields
				if (tm.is_trial) props._is_trial = tm.is_trial
				if (tm.trial_term) props._trial_term = tm.trial_term
				if (tm.trial_unit) props._trial_unit = tm.trial_unit
			}

			const item: PredictedCartItem = {
				id: membershipSelection.rule.giftProduct.variantId,
				quantity: 1,
				properties: props,
			}

			if (
				membershipSelection.rule.giftProduct.sellingPlanId &&
				!membershipSelection.rule.giftProduct.isOneTimeMembership
			) {
				item.selling_plan =
					membershipSelection.rule.giftProduct.sellingPlanId
			}

			items.push(item)
		}
	}

	return items
}
